import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import ViewsChart from "../../components/ViewsChart";
import VisitorWorldMap from "../../components/VisitorWorldMap";
import { getApiUrl } from "../../utils/api";
import "./DashboardView.css";

function DashboardView() {
  // Firebase metrics
  const [views, setViews] = useState(0);
  const [downloads, setDownloads] = useState(0);
  const [countries, setCountries] = useState({});
  const [devices, setDevices] = useState({ desktop: 0, mobile: 0, tablet: 0 });

  // MongoDB metrics
  const [mongoStats, setMongoStats] = useState({
    contactCount: 0,
    feedbackCount: 0,
    ticketSummary: { received: 0, inProgress: 0, resolved: 0, closed: 0 },
    recentContacts: [],
    recentFeedbacks: []
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);

      // 1. Firebase Analytics
      try {
        const viewRef = doc(db, "analytics", "portfolioViews");
        const viewSnap = await getDoc(viewRef);
        if (viewSnap.exists()) setViews(viewSnap.data().count || 0);
      } catch (err) {
        console.error("Firebase views fetch error:", err);
      }

      try {
        const downloadRef = doc(db, "analytics", "resumeDownloads");
        const downloadSnap = await getDoc(downloadRef);
        if (downloadSnap.exists()) setDownloads(downloadSnap.data().count || 0);
      } catch (err) {
        console.error("Firebase downloads fetch error:", err);
      }

      try {
        const visitorRef = doc(db, "visitors", "visitorData");
        const visitorSnap = await getDoc(visitorRef);
        if (visitorSnap.exists()) setCountries(visitorSnap.data().countries || {});
      } catch (err) {
        console.error("Firebase visitor countries fetch error:", err);
      }

      try {
        const deviceRef = doc(db, "analytics", "devices");
        const deviceSnap = await getDoc(deviceRef);
        if (deviceSnap.exists()) setDevices(deviceSnap.data() || {});
      } catch (err) {
        console.error("Firebase device analytics fetch error:", err);
      }

      // 2. MongoDB Stats & Recent Submissions
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(getApiUrl("/admin/stats"), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        if (data.success && data.stats) {
          setMongoStats(data.stats);
        }
      } catch (err) {
        console.error("MongoDB stats fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const totalVisitors = (devices.desktop || 0) + (devices.mobile || 0) + (devices.tablet || 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case "In Progress":
        return <span className="status-badge badge-progress">In Progress</span>;
      case "Resolved":
        return <span className="status-badge badge-resolved">Resolved</span>;
      case "Closed":
        return <span className="status-badge badge-closed">Closed</span>;
      default:
        return <span className="status-badge badge-received">Received</span>;
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner-box" style={{ padding: "80px", textAlign: "center" }}>
        <div className="spinner"></div>
        <p style={{ color: "#38bdf8", marginTop: "12px" }}>Initializing Admin Dashboard Metrics...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-view-container">
      {/* STAT CARDS GRID */}
      <div className="dash-metrics-grid">
        {/* FIREBASE CARDS */}
        <div className="dash-card glass-glow">
          <div className="card-header-row">
            <span className="card-icon">👀</span>
            <span className="source-tag firebase-tag">Firebase</span>
          </div>
          <h3>Portfolio Views</h3>
          <p className="card-value">{views.toLocaleString()}</p>
          <span className="card-subtext">Total page impressions</span>
        </div>

        <div className="dash-card glass-glow">
          <div className="card-header-row">
            <span className="card-icon">📄</span>
            <span className="source-tag firebase-tag">Firebase</span>
          </div>
          <h3>Resume Downloads</h3>
          <p className="card-value">{downloads.toLocaleString()}</p>
          <span className="card-subtext">CV download count</span>
        </div>

        <div className="dash-card glass-glow">
          <div className="card-header-row">
            <span className="card-icon">🌐</span>
            <span className="source-tag firebase-tag">Firebase</span>
          </div>
          <h3>Total Visitors</h3>
          <p className="card-value">{totalVisitors.toLocaleString()}</p>
          <span className="card-subtext">{Object.keys(countries).length} Countries</span>
        </div>

        {/* MONGODB CARDS */}
        <div className="dash-card glass-glow">
          <div className="card-header-row">
            <span className="card-icon">📩</span>
            <span className="source-tag mongo-tag">MongoDB</span>
          </div>
          <h3>Contact Messages</h3>
          <p className="card-value">{mongoStats.contactCount}</p>
          <span className="card-subtext">Inquiries & Tickets</span>
        </div>

        <div className="dash-card glass-glow">
          <div className="card-header-row">
            <span className="card-icon">💬</span>
            <span className="source-tag mongo-tag">MongoDB</span>
          </div>
          <h3>Feedback Submissions</h3>
          <p className="card-value">{mongoStats.feedbackCount}</p>
          <span className="card-subtext">Detailed User Feedbacks</span>
        </div>
      </div>

      {/* TICKET SUMMARY BREAKDOWN & DEVICES */}
      <div className="dash-two-col">
        {/* TICKET SUMMARY */}
        <div className="dash-glass-panel">
          <div className="panel-title-row">
            <h2>🎫 Ticket Summary</h2>
            <Link to="/admin/contact-messages" className="view-all-link">View All →</Link>
          </div>
          <div className="ticket-summary-grid">
            <div className="ticket-box box-received">
              <span className="t-count">{mongoStats.ticketSummary?.received || 0}</span>
              <span className="t-label">Received</span>
            </div>
            <div className="ticket-box box-progress">
              <span className="t-count">{mongoStats.ticketSummary?.inProgress || 0}</span>
              <span className="t-label">In Progress</span>
            </div>
            <div className="ticket-box box-resolved">
              <span className="t-count">{mongoStats.ticketSummary?.resolved || 0}</span>
              <span className="t-label">Resolved</span>
            </div>
            <div className="ticket-box box-closed">
              <span className="t-count">{mongoStats.ticketSummary?.closed || 0}</span>
              <span className="t-label">Closed</span>
            </div>
          </div>
        </div>

        {/* DEVICE BREAKDOWN */}
        <div className="dash-glass-panel">
          <div className="panel-title-row">
            <h2>💻 Device Breakdown</h2>
            <span className="source-tag firebase-tag">Firebase</span>
          </div>
          <div className="device-list">
            <div className="device-item">
              <span className="dev-name">🖥️ Desktop</span>
              <div className="dev-bar-wrapper">
                <div 
                  className="dev-bar desktop-bar" 
                  style={{ width: `${totalVisitors ? Math.round(((devices.desktop || 0) / totalVisitors) * 100) : 0}%` }}
                />
              </div>
              <span className="dev-count">{devices.desktop || 0}</span>
            </div>

            <div className="device-item">
              <span className="dev-name">📱 Mobile</span>
              <div className="dev-bar-wrapper">
                <div 
                  className="dev-bar mobile-bar" 
                  style={{ width: `${totalVisitors ? Math.round(((devices.mobile || 0) / totalVisitors) * 100) : 0}%` }}
                />
              </div>
              <span className="dev-count">{devices.mobile || 0}</span>
            </div>

            <div className="device-item">
              <span className="dev-name">📟 Tablet</span>
              <div className="dev-bar-wrapper">
                <div 
                  className="dev-bar tablet-bar" 
                  style={{ width: `${totalVisitors ? Math.round(((devices.tablet || 0) / totalVisitors) * 100) : 0}%` }}
                />
              </div>
              <span className="dev-count">{devices.tablet || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div className="dash-two-col">
        <div className="dash-glass-panel">
          <div className="panel-title-row">
            <h2>📊 Weekly Views Timeline</h2>
          </div>
          <ViewsChart />
        </div>

        <div className="dash-glass-panel">
          <div className="panel-title-row">
            <h2>🌍 Visitor World Map</h2>
          </div>
          <VisitorWorldMap hideTitle={true} />
        </div>
      </div>

      {/* RECENT CONTACT MESSAGES */}
      <div className="dash-glass-panel full-width-panel">
        <div className="panel-title-row">
          <h2>📩 Recent Contact Messages</h2>
          <Link to="/admin/contact-messages" className="view-all-link">Manage Messages →</Link>
        </div>

        {mongoStats.recentContacts && mongoStats.recentContacts.length > 0 ? (
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date & Time</th>
                  <th>User Name</th>
                  <th>Ticket ID</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {mongoStats.recentContacts.map((contact, idx) => (
                  <tr key={contact._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{new Date(contact.createdAt).toLocaleString()}</td>
                    <td className="user-name-cell">
                      {contact.firstName} {contact.lastName}
                      <span className="email-sub">{contact.email}</span>
                    </td>
                    <td><code className="ticket-code">{contact.trackingId}</code></td>
                    <td>{getStatusBadge(contact.status)}</td>
                    <td>
                      <a
                        href={`/contact-messages/${contact.trackingId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-action-view"
                      >
                        View Details 👁️
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>No recent contact messages found.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardView;
